import { Code, Group, Stack, Table, Text, Tooltip } from '@mantine/core'
import dayjs from 'dayjs'
import { cloneElement } from 'react'
import { FormattedMessage } from 'react-intl'
import { DataTable } from '@/components/ui-shared/Tables/DataTable'
import { VlmResult } from '@/types/vlm'
import { formatDuration, getFormattedDateWithTime } from '@/utils/date'
import { VlmResultTags } from './VlmResultTags'
import { VlmResultThumbnail } from './VlmResultThumbnail'
import { PIPELINE_TYPE_CONFIG } from './vlmPipelineConfig'

type VlmResultListProps = {
  results: VlmResult[]
  isLoading?: boolean
  hasError?: boolean
  onReload?: () => void
  onResultClick?: (result: VlmResult) => void
}

export const VlmResultList = ({
  results,
  isLoading,
  hasError,
  onReload,
  onResultClick
}: VlmResultListProps) => {
  return (
    <DataTable
      hasItems={results.length > 0}
      isLoading={isLoading}
      hasError={hasError}
      emptyMessage={<FormattedMessage id="vlm.results.empty" />}
      onReload={onReload}
    >
      <Table.Thead>
        <Table.Tr>
          <Table.Th w={116}>
            <FormattedMessage id="vlm.results.fields.preview" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.results.fields.eventId" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.results.fields.pipeline" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.results.fields.timestamp" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.results.fields.description" />
          </Table.Th>
          <Table.Th>
            <FormattedMessage id="vlm.results.fields.duration" />
          </Table.Th>
        </Table.Tr>
      </Table.Thead>

      <Table.Tbody>
        {results.map((result) => {
          // pipeline_source is null for an event whose pipeline was deleted —
          // Mongo events outlive the Postgres row, so the lookup can miss.
          const typeConfig = result.pipeline_source
            ? PIPELINE_TYPE_CONFIG[result.pipeline_source]
            : undefined

          return (
            <Table.Tr
              key={result.id}
              style={{ cursor: 'pointer' }}
              onClick={() => onResultClick?.(result)}
            >
              <Table.Td>
                <VlmResultThumbnail result={result} />
              </Table.Td>

              <Table.Td>
                <Code>{result.id}</Code>
              </Table.Td>

              <Table.Td>
                <Group gap={6} wrap="nowrap" align="center">
                  {typeConfig && cloneElement(typeConfig.icon, { size: 14 })}
                  <Text size="sm">
                    {result.pipeline_name ?? (
                      <FormattedMessage id="vlm.results.pipelineDeleted" />
                    )}
                  </Text>
                </Group>
              </Table.Td>

              <Table.Td>
                <Text size="sm">
                  {getFormattedDateWithTime(result.created_at)}
                </Text>
              </Table.Td>

              <Table.Td maw={400}>
                <Stack gap={6}>
                  <Tooltip
                    label={result.result.description}
                    maw={400}
                    openDelay={300}
                    multiline
                  >
                    <Text size="xs" lineClamp={2}>
                      {result.result.description}
                    </Text>
                  </Tooltip>

                  <VlmResultTags
                    tags={result.result.tags}
                    alertTags={result.result.alert_tags}
                  />
                </Stack>
              </Table.Td>

              <Table.Td>
                <Text size="sm">
                  {formatDuration(
                    dayjs(result.result.created_at).diff(
                      dayjs(result.result.timestamp)
                    )
                  )}
                </Text>
              </Table.Td>
            </Table.Tr>
          )
        })}
      </Table.Tbody>
    </DataTable>
  )
}
